import React, { useMemo } from 'react';
import { Box, Text } from '@common-fe/common-fe';

import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';

import { Renewal } from '../../renewals.types';
import { RenewalPlanOptionValues, RenewalSetupStep, RenewalSetupStepTitle } from '../RenewalSetup.types';
import { useRenewalSetupStore } from '../stores';
import useHealthPlansStore from '../stores/useHealthPlans.store';

import ProgressBarItem from './ProgressBarItem';

interface Props {
  stepName?: RenewalSetupStep;
  renewalSetupData?: Renewal;
  handleStepName: (stepName: RenewalSetupStep) => void;
}

const ProgressBar: React.FC<Props> = ({ stepName, renewalSetupData, handleStepName }) => {
  const isHealthPlanRenewalEnabled = useFeatureFlag(FeatureFlags.HEALTH_PLAN_RENEWAL);
  const healthPlans = useHealthPlansStore((state) => state.state.healthPlans);
  const hasHealthPlans = (healthPlans?.length || 0) > 0;
  const isPlansRenewOptionSubmitted = useRenewalSetupStore((state) => state.plansPlanYear.isSubmitted);
  const plansRenewalSelectedOption = useRenewalSetupStore((state) => state.plansPlanYear.selectedOption);
  const isHealthPlansRenewOptionSubmitted = useRenewalSetupStore((state) => state.healthPlans.isSubmitted);
  const isCopaysRenewOptionSubmitted = useRenewalSetupStore((state) => state.copays.isSubmitted);
  const isPlansRenewOptionFailed = useRenewalSetupStore((state) => state.plansPlanYear.isFailed);
  const isHealthPlansRenewOptionFailed = useRenewalSetupStore((state) => state.healthPlans.isFailed);
  const isRevalidationPending = useRenewalSetupStore((state) => state.isRevalidationPending);

  const progressBarOptions = useMemo(
    () =>
      plansRenewalSelectedOption === RenewalPlanOptionValues.CANCELED
        ? [
            {
              title: RenewalSetupStepTitle[RenewalSetupStep.PlansPlanYear],
              isEntityExist: isPlansRenewOptionSubmitted,
              isFailed: isPlansRenewOptionFailed,
              stepName: RenewalSetupStep.PlansPlanYear,
              isActive: stepName === RenewalSetupStep.PlansPlanYear,
            },
          ]
        : [
            {
              title: RenewalSetupStepTitle[RenewalSetupStep.PlansPlanYear],
              isEntityExist: isPlansRenewOptionSubmitted,
              isFailed: isPlansRenewOptionFailed,
              stepName: RenewalSetupStep.PlansPlanYear,
              isActive: stepName === RenewalSetupStep.PlansPlanYear,
            },
            ...(isHealthPlanRenewalEnabled && hasHealthPlans
              ? [
                  {
                    title: RenewalSetupStepTitle[RenewalSetupStep.HealthPlans],
                    isEntityExist: isHealthPlansRenewOptionSubmitted,
                    isFailed: isHealthPlansRenewOptionFailed,
                    stepName: RenewalSetupStep.HealthPlans,
                    isActive: stepName === RenewalSetupStep.HealthPlans,
                  },
                ]
              : []),
            {
              title: RenewalSetupStepTitle[RenewalSetupStep.Copays],
              isEntityExist: isCopaysRenewOptionSubmitted,
              isLastItem: isCopaysRenewOptionSubmitted,
              stepName: RenewalSetupStep.Copays,
              isActive: stepName === RenewalSetupStep.Copays,
            },
          ],
    [
      isCopaysRenewOptionSubmitted,
      isHealthPlanRenewalEnabled,
      hasHealthPlans,
      isHealthPlansRenewOptionSubmitted,
      isHealthPlansRenewOptionFailed,
      isPlansRenewOptionSubmitted,
      isPlansRenewOptionFailed,
      plansRenewalSelectedOption,
      stepName,
    ]
  );

  return (
    <Box
      width="304px"
      round="container1Round"
      style={{ position: 'sticky', overflow: 'hidden' }}
      data-testid="renewal-setup-progress-bar"
      pad={{ horizontal: 's', bottom: 's', top: 'm' }}
      margin={{ bottom: 's' }}
      background="module"
    >
      <Box direction="row" justify="between" pad={{ horizontal: 's' }}>
        <Text size="16px" weight={700} style={{ lineHeight: '22px' }} color="textTitle">
          Progress
        </Text>
      </Box>
      <Box
        width="100%"
        round="container1Round"
        background="canvas"
        pad={{ horizontal: 's', vertical: 'm' }}
        margin={{ vertical: 's' }}
        gap="spacing12"
      >
        {progressBarOptions.map((item, index) => {
          const isPreviousStepCompleted = index === 0 || progressBarOptions[index - 1].isEntityExist;
          const isBlockedByRevalidation = isRevalidationPending && !item.isActive;
          const isDisabled =
            (!item.isActive && !item.isEntityExist && !isPreviousStepCompleted) || isBlockedByRevalidation;

          return (
            <ProgressBarItem
              key={index}
              stepName={item.stepName}
              title={item.title}
              isLastItem={item.isLastItem}
              isEntityExist={item.isEntityExist}
              isFailed={item.isFailed}
              isActive={item.isActive}
              copays={renewalSetupData?.copays}
              isDisabled={isDisabled}
              isValidating={isBlockedByRevalidation}
              handleStepName={handleStepName}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ProgressBar;
